import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Button,
  Paper,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { format, isBefore } from 'date-fns';
import { useApp } from '../../context/AppContext';
import { useTranslation } from 'react-i18next';

const CancelBookingPage: React.FC = () => {
  const { appointmentId } = useParams<{ appointmentId: string }>();
  const navigate = useNavigate();
  const { refreshAppointments } = useApp();
  const { t } = useTranslation();

  const [appointment, setAppointment] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancellationReason, setCancellationReason] = useState('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const loadAppointment = async () => {
      if (!appointmentId) {
        setError(t('cancel.errors.invalidId'));
        setLoading(false);
        return;
      }

      try {
        const appointmentDoc = await getDoc(doc(db, 'appointments', appointmentId));

        if (!appointmentDoc.exists()) {
          setError(t('cancel.errors.notFound'));
          setLoading(false);
          return;
        }

        const appointmentData: any = { id: appointmentDoc.id, ...appointmentDoc.data() };

        const appointmentDateTime = new Date(`${appointmentData.date}T${appointmentData.startTime}:00`);
        if (isBefore(appointmentDateTime, new Date())) {
          setError(t('cancel.errors.passed'));
          setLoading(false);
          return;
        }

        if (appointmentData.status === 'cancelled') {
          setError(t('cancel.errors.alreadyCancelled'));
          setLoading(false);
          return;
        }

        setAppointment(appointmentData);
        setLoading(false);
      } catch (err) {
        setError(t('cancel.errors.failedLoad'));
        setLoading(false);
      }
    };

    loadAppointment();
  }, [appointmentId, t]);

  const handleCancelClick = () => setShowConfirmDialog(true);

  const handleConfirmCancel = async () => {
    if (!appointment) return;
    setIsCancelling(true);
    setShowConfirmDialog(false);

    try {
      const appointmentRef = doc(db, 'appointments', appointmentId!);
      await updateDoc(appointmentRef, {
        status: 'cancelled',
        cancelledAt: new Date().toISOString(),
        cancellationReason: cancellationReason || 'No reason provided',
        updatedAt: new Date().toISOString(),
      });

      await refreshAppointments();
      setSuccess(true);

      setTimeout(() => navigate('/'), 5000);
    } catch (err) {
      setError(t('cancel.errors.failedCancel'));
      setIsCancelling(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>{t('cancel.loading')}</Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5" color="error" gutterBottom>
            {t('cancel.errorTitle')}
          </Typography>
          <Alert severity="error" sx={{ my: 3 }}>{error}</Alert>
          <Typography variant="body1" paragraph>{t('cancel.contactHelp')}</Typography>
          <Typography variant="body1" gutterBottom>{t('cancel.phone')}</Typography>
          <Typography variant="body1" gutterBottom>{t('cancel.email')}</Typography>
          <Button variant="contained" onClick={() => navigate('/')} sx={{ mt: 3 }}>
            {t('cancel.returnHome')}
          </Button>
        </Paper>
      </Container>
    );
  }

  if (success) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h4" color="success.main" gutterBottom>
            {t('cancel.successTitle')}
          </Typography>
          <Alert severity="success" sx={{ my: 3 }}>{t('cancel.successAlert')}</Alert>
          <Typography variant="body1" paragraph>{t('cancel.successMessage')}</Typography>
          <Box sx={{ mt: 4 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate('/booking')}
              sx={{ mr: 2 }}
            >
              {t('cancel.bookAnother')}
            </Button>
            <Button variant="outlined" onClick={() => navigate('/')}>
              {t('cancel.returnHome')}
            </Button>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
            {t('cancel.redirecting')}
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom align="center" color="error">
          {t('cancel.title')}
        </Typography>

        <Alert severity="warning" sx={{ my: 3 }}>
          {t('cancel.warning')}
        </Alert>

        <Paper sx={{ p: 3, my: 3, bgcolor: 'grey.50' }}>
          <Typography variant="h6" gutterBottom>
            {t('cancel.detailsTitle')}
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1">
              <strong>{t('cancel.service')}:</strong> {appointment.serviceName}
            </Typography>
            <Typography variant="body1">
              <strong>{t('cancel.date')}:</strong> {format(new Date(appointment.date), 'EEEE, MMMM d, yyyy')}
            </Typography>
            <Typography variant="body1">
              <strong>{t('cancel.time')}:</strong> {appointment.startTime} - {appointment.endTime}
            </Typography>
            <Typography variant="body1">
              <strong>{t('cancel.duration')}:</strong> {appointment.duration} {t('cancel.durationUnit')}
            </Typography>
            <Typography variant="body1">
              <strong>{t('cancel.client')}:</strong> {appointment.clientName || `${appointment.firstName || ''} ${appointment.lastName || ''}`.trim() || 'Client'}
            </Typography>
          </Box>
        </Paper>

        <Box sx={{ my: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            {t('cancel.reasonLabel')}
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder={t('cancel.reasonPlaceholder')}
            value={cancellationReason}
            onChange={(e) => setCancellationReason(e.target.value)}
            variant="outlined"
          />
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button variant="outlined" size="large" onClick={() => navigate('/')}>
            {t('cancel.keepAppointment')}
          </Button>
          <Button variant="contained" color="error" size="large" onClick={handleCancelClick}>
            {t('cancel.confirmCancellation')}
          </Button>
        </Box>

        <Box sx={{ mt: 4, pt: 3, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="body2" color="text.secondary" align="center">
            {t('cancel.modifyContact')}
          </Typography>
        </Box>
      </Paper>

      <Dialog open={showConfirmDialog} onClose={() => setShowConfirmDialog(false)}>
        <DialogTitle>{t('cancel.dialogTitle')}</DialogTitle>
        <DialogContent>
          <Typography>{t('cancel.dialogText')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirmDialog(false)}>
            {t('cancel.dialogNo')}
          </Button>
          <Button onClick={handleConfirmCancel} color="error" variant="contained">
            {t('cancel.dialogYes')}
          </Button>
        </DialogActions>
      </Dialog>

      {isCancelling && (
        <Box
          sx={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 9999,
          }}
        >
          <Paper
            elevation={3}
            sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}
          >
            <CircularProgress />
            <Typography>{t('cancel.cancelling')}</Typography>
          </Paper>
        </Box>
      )}
    </Container>
  );
};

export default CancelBookingPage;
